import { useState, type KeyboardEvent } from 'react';
import { fetchIncidentEventsRuleQuery, runIncidentEventsRuleQuery, type IncidentEventsRuleQueryResult } from '../lib/incidentEventsRuleQueryApi';
import { fmtTs } from '../lib/globalTz';
import { KqlIntellisenseEditor } from './intelligence/KqlIntellisenseEditor';
import { DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE } from '../lib/kqlCatalog';
import { AdvancedResultsGrid } from './AdvancedResultsGrid';

const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

function formatCellForDisplay(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && ISO_TIMESTAMP_RE.test(value)) return fmtTs(value);
  return String(value);
}

function csvEscape(value: string): string {
  const raw = value.replace(/\r?\n|\r/g, ' ').replace(/\s{2,}/g, ' ').trim();
  return /[",\n\r]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
}

function safeFilenamePart(value: string): string {
  return value.replace(/[^a-z0-9_-]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase();
}

function downloadBlob(content: string, mime: string, filename: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportRawLogsCsv(result: IncidentEventsRuleQueryResult) {
  const columns = result.columns ?? [];
  const rows = result.rows ?? [];
  const csvRows = [
    columns.map(csvEscape).join(','),
    ...rows.map((row) => columns.map((_c, i) => csvEscape(formatCellForDisplay(row[i]))).join(',')),
  ];
  const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const numberPart = safeFilenamePart(result.incidentNumber || result.incidentId || 'incident');
  downloadBlob(csvContent, 'text/csv;charset=utf-8;', `events-evidence-${numberPart}-${timestamp}.csv`);
}

function exportRawLogsJson(result: IncidentEventsRuleQueryResult) {
  const columns = result.columns ?? [];
  const rows = result.rows ?? [];
  const records = rows.map((row) => {
    const record: Record<string, string> = {};
    columns.forEach((c, i) => { record[c] = formatCellForDisplay(row[i]); });
    return record;
  });
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const numberPart = safeFilenamePart(result.incidentNumber || result.incidentId || 'incident');
  downloadBlob(JSON.stringify(records, null, 2), 'application/json;charset=utf-8;', `events-evidence-${numberPart}-${timestamp}.json`);
}

export function IncidentEventsRuleQueryPanel(props: {
  incidentId: string;
  customerId?: string;
  armIncidentName?: string;
  incidentNumber?: string;
  onOpenQuery?: (query: string) => void;
  canBuildQuery?: boolean;
  canSetQuery?: boolean;
  canRunQuery?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IncidentEventsRuleQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editedQuery, setEditedQuery] = useState('');
  const [builtQuery, setBuiltQuery] = useState<string | null>(null);

  const queryReady = !!editedQuery.trim();
  const canBuild = props.canBuildQuery !== false;
  const canRun = props.canRunQuery !== false;
  const canSet = props.canSetQuery !== false;
  const hasRows = !!(result?.columns && result?.rows && result.rows.length > 0);
  const isEdited = builtQuery != null && editedQuery !== builtQuery;

  function requestArgs() {
    return {
      incidentId: props.incidentId,
      customerId: props.customerId,
      armIncidentName: props.armIncidentName,
      incidentNumber: props.incidentNumber,
      limit: 200,
    };
  }

  async function buildOnly() {
    if (!canBuild) {
      setError('Your role is not allowed to build Events Evidence queries. Ask an administrator to enable Incidents / Build Events Evidence Query.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetchIncidentEventsRuleQuery(requestArgs());
      setResult(r);
      setBuiltQuery(r.query);
      setEditedQuery(r.query);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function runNow() {
    if (!canRun) {
      setError('Your role is not allowed to run Events Evidence queries. Ask an administrator to enable Incidents / Run Events Evidence Query.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const queryOverride = editedQuery.trim() || undefined;
      const r = await runIncidentEventsRuleQuery({ ...requestArgs(), query: queryOverride });
      setResult(r);
      if (builtQuery == null) setBuiltQuery(r.query);
      setEditedQuery(r.query);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Match the analyst shortcuts used in Microsoft Sentinel / Log Analytics
  // workbooks: Ctrl+Enter continues a query pipeline on the next line,
  // Shift+Enter runs the query — same convention as Advanced Hunting.
  function handleKqlEditorKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return;

    if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart ?? editedQuery.length;
      const end = textarea.selectionEnd ?? start;
      const insertion = '\n| ';
      const nextQuery = `${editedQuery.slice(0, start)}${insertion}${editedQuery.slice(end)}`;
      setEditedQuery(nextQuery);

      window.setTimeout(() => {
        const caret = start + insertion.length;
        textarea.selectionStart = caret;
        textarea.selectionEnd = caret;
      }, 0);
      return;
    }

    if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      void runNow();
    }
  }

  function resetToGenerated() {
    if (builtQuery != null) setEditedQuery(builtQuery);
  }

  function openInAdvancedHunting() {
    if (!editedQuery.trim()) return;
    if (!canSet) {
      navigator.clipboard.writeText(editedQuery).catch(() => null);
      setError('Your role is not allowed to set Advanced Hunting queries from pivots. The query was copied to clipboard instead.');
      return;
    }
    props.onOpenQuery?.(editedQuery);
  }

  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Events Evidence / Rule Query</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Resolves linked Sentinel alerts, finds the analytic rule or embedded alert query, then builds an editable Events Evidence KQL query you can tweak before running.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="rounded-lg border px-3 py-1 text-xs disabled:opacity-50" disabled={loading || !canBuild} onClick={buildOnly} title={canBuild ? 'Build generated Events Evidence query' : 'Requires Incidents / Build Events Evidence Query'}>Build query</button>
          <button
            className="rounded-lg bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
            disabled={loading || !canRun}
            onClick={runNow}
            title={canRun ? 'Run the query below (including any edits)' : 'Requires Incidents / Run Events Evidence Query'}
          >
            Run query
          </button>
          <button
            className="rounded-lg border px-3 py-1 text-xs disabled:opacity-50"
            disabled={loading || !queryReady || !props.onOpenQuery}
            onClick={openInAdvancedHunting}
            title={canSet ? 'Open generated query in Advanced Hunting' : 'Requires Advanced Hunting / Set Query from Pivot'}
          >
            Open in Advanced Hunting
          </button>
        </div>
      </div>
      {error && <div className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</div>}
      {result && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1 text-xs text-slate-600 dark:text-slate-300 break-words">
              Mode: <b>{result.mode}</b> · Source: <b>{result.querySource}</b> · Alerts: <b>{result.alerts?.length ?? 0}</b> · Rules: <b>{result.ruleQueries?.length ?? 0}</b> · Rows: <b>{result.rowCount ?? '-'}</b>
            </div>
            {hasRows && (
              <div className="flex flex-shrink-0 flex-wrap gap-2">
                <button
                  className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={() => exportRawLogsCsv(result)}
                  title="Download raw logs as CSV, with timestamps in the selected time zone"
                >
                  Download Raw Logs (CSV)
                </button>
                <button
                  className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={() => exportRawLogsJson(result)}
                  title="Download raw logs as JSON, with timestamps in the selected time zone"
                >
                  Download Raw Logs (JSON)
                </button>
              </div>
            )}
          </div>
          {result.diagnostics?.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-300">
              {result.diagnostics.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Query (editable)</label>
              {isEdited && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                  onClick={resetToGenerated}
                  title="Discard edits and restore the auto-generated query"
                >
                  Reset to generated query
                </button>
              )}
            </div>
            <KqlIntellisenseEditor
              className="max-h-72 min-h-[8rem] w-full overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editedQuery}
              onChange={setEditedQuery}
              onKeyDown={handleKqlEditorKeyDown}
              source="sentinel"
              disabled={loading}
              schemaTables={DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE}
            />
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 font-mono">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 font-mono">Enter</kbd> inserts a new pipeline line, <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 font-mono">Shift</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 font-mono">Enter</kbd> runs the query.
            </div>
          </div>
          {result.columns && result.rows && (
            <AdvancedResultsGrid
              columns={result.columns}
              rows={result.rows}
              storageKey={`incident-events-evidence-${props.incidentId}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
